/**
 * Release management API client using oRPC.
 */

import type { Result } from "true-myth/result";

import type {
  FreezeStatus,
  ListFreezesResponse,
  ReleaseListResponse,
  ReleasePlatform,
  SetActiveReleaseResponse,
} from "@animus/api-treaty";

import type { AuthStrategy } from "../../config/api-config";
import {
  callApi,
  createApiClient,
  type OrpcApiError,
} from "../../config/orpc-client";

export function createReleasesApi(baseUrl: string, auth: AuthStrategy) {
  const api = createApiClient(baseUrl, auth);

  async function listReleases(options?: {
    platform?: ReleasePlatform | "all";
    limit?: number;
  }): Promise<Result<ReleaseListResponse, OrpcApiError>> {
    if (options?.platform && options.platform !== "all") {
      return await callApi(
        api.release.listPlatform({
          platform: options.platform,
          limit: options.limit,
        }),
        "Failed to fetch releases"
      );
    }

    return await callApi(
      api.release.list({ limit: options?.limit }),
      "Failed to fetch releases"
    );
  }

  async function getFreezeStatus(): Promise<
    Result<ListFreezesResponse, OrpcApiError>
  > {
    return await callApi(
      api.deploy.listFreezes({}),
      "Failed to fetch freeze status"
    );
  }

  async function freezeApp(
    app: ReleasePlatform,
    pinnedVersion: string,
    reason?: string
  ): Promise<Result<{ freeze: FreezeStatus }, OrpcApiError>> {
    return await callApi(
      api.deploy.freeze({ app, pinnedVersion, reason }),
      "Failed to freeze app"
    );
  }

  async function unfreezeApp(
    app: ReleasePlatform
  ): Promise<Result<{ freeze: FreezeStatus }, OrpcApiError>> {
    return await callApi(
      api.deploy.unfreeze({ app }),
      "Failed to unfreeze app"
    );
  }

  async function setActiveRelease(
    platform: ReleasePlatform,
    version: string,
    acknowledgeIncompatibility?: boolean
  ): Promise<Result<SetActiveReleaseResponse, OrpcApiError>> {
    return await callApi(
      api.release.setActive({ platform, version, acknowledgeIncompatibility }),
      "Failed to set active release"
    );
  }

  return {
    listReleases,
    getFreezeStatus,
    freezeApp,
    unfreezeApp,
    setActiveRelease,
  };
}

export type ReleasesApi = ReturnType<typeof createReleasesApi>;
